import { useEffect, useState } from "react";
import { modelLabel } from "../../shared/models";
import type { ApprovalDTO, ScheduleHandoverPayload } from "../../shared/types";
import { ApiError, get, post } from "../lib/api";
import { cronPreview } from "../lib/cron-words";
import { AgentAvatar } from "./AgentAvatar";
import { whenLine } from "./Schedules";
import { Badge, Button, Collapsible, ErrorText, inputCls } from "./ui";
import { AddressedTo, Decided } from "./Verdicts";

/** When it runs, in words where the cron has a common shape: "Every weekday at 09:00 (Europe/Athens)". */
function whenWords(p: ScheduleHandoverPayload): string {
  if (p.trigger !== "cron" || !p.cron) return whenLine(p);
  const preview = cronPreview(p.cron, p.timezone);
  return "error" in preview || !preview.words ? whenLine(p) : `${preview.words} (${preview.zone})`;
}

/**
 * A schedule someone hands to this person (AIT-62): who offers it, on which agent, when it runs, where its answer goes
 * once it is theirs, what it asks, and that it will run as them with their own logins. Accept makes it theirs; Decline,
 * with an optional note for whoever offered it, leaves it as it was. Only the person it is offered to decides it: anyone
 * else, an admin included, reads it with the buttons drawn off. The same card in their Pandito chat and on Requests.
 */
export function HandoverCard({ approval, canDecide, onDecided }: { approval: ApprovalDTO; canDecide: boolean; onDecided?: (updated: ApprovalDTO) => void }) {
  // What we just decided holds until the server's own copy arrives (the websocket, or the next load).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setLocal(null), [approval]);
  const a = local ?? approval;
  // Anyone but an admin and the person it is offered to reads the card without what was offered.
  const p = (a.payload?.offeredBy ? a.payload : null) as ScheduleHandoverPayload | null;
  const offerer = p?.offeredBy.name ?? "Someone";
  const you = canDecide ? "you" : (a.ownerName ?? "someone");

  const decide = async (decision: "approve" | "reject") => {
    setBusy(true);
    setError(null);
    try {
      const decided = await post<ApprovalDTO>(`/api/approvals/${a.id}/decide`, { decision, note: note.trim() || undefined });
      setLocal(decided);
      onDecided?.(decided);
    } catch (err) {
      // Withdrawn, or the schedule changed meanwhile: show where it actually stands instead of an error.
      const fresh = err instanceof ApiError && err.status === 409 ? await get<ApprovalDTO>(`/api/approvals/${a.id}`).catch(() => null) : null;
      if (fresh && fresh.status !== "pending") return setLocal(fresh);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Once it is theirs, its own chat with the agent is its fixed line, so that place is never listed twice.
  const elsewhere = (p?.destinations ?? []).filter((d) => !(d.kind === "web" && d.userId === a.ownerUserId)).map((d) => d.words);
  const rows: [string, React.ReactNode][] = p
    ? [
        ["Agent", p.agentName],
        ["When", whenWords(p)],
        ...(p.model ? [["Model", modelLabel(p.model)] as [string, React.ReactNode]] : []),
        ["Answers in", [`${canDecide ? "your" : `${you}'s`} own chat with ${p.agentName}`, ...elsewhere].join(", ")],
        ["What it asks", <Collapsible key="prompt" summary={<span className="line-clamp-2 text-sm text-muted">{p.prompt}</span>}><p className="mt-1 text-sm whitespace-pre-wrap text-muted">{p.prompt}</p></Collapsible>],
      ]
    : [];

  return (
    <div className="card border-l-2 border-l-haze-deep p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <AgentAvatar agent={a.agentId} name={a.agentName} size="sm" />
        <Badge tone="blue">Schedule</Badge>
        <span className="text-muted">
          <span className="font-medium text-fg">{offerer}</span> offers {you} the schedule “{a.title}” on {a.agentName}
        </span>
      </div>
      {rows.length > 0 && (
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-xl bg-fill-subtle p-3 text-sm sm:grid-cols-[7rem_minmax(0,1fr)]">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-xs font-medium text-muted sm:pt-0.5">{k}</dt>
              <dd className="min-w-0 break-words text-muted">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {a.status === "pending" && (
        <p className="mt-3 text-sm text-fg">{canDecide ? "Once you accept, it runs as you, with your own logins." : `Once ${you} accepts, it runs as them, with their own logins.`}</p>
      )}
      {a.status !== "pending" ? (
        <Decided a={a} />
      ) : canDecide ? (
        <div className="mt-4 space-y-2">
          <textarea className={inputCls} rows={2} placeholder={`A note for ${offerer} if you decline (optional)`} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled={busy} onClick={() => decide("approve")}>
              Accept
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => decide("reject")}>
              Decline
            </Button>
          </div>
          <p className="text-xs text-faint">Until you answer, it does not run.</p>
          <ErrorText error={error} />
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled>
              Accept
            </Button>
            <Button variant="danger" disabled>
              Decline
            </Button>
          </div>
          <AddressedTo a={a} suffix=", who takes it over by accepting" />
        </div>
      )}
    </div>
  );
}
